
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Users, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ChatGroupMembers from './ChatGroupMembers';
import MessagesList from './MessagesList';
import ChatInputArea from './ChatInputArea';
import MentionNavigator from './MentionNavigator';

interface MobileChatViewProps {
  selectedGroup: any;
  messages: any[];
  user: any;
  sendMessage: (message: string, parentMessageId?: string) => Promise<void>;
  canCreateGroup: boolean;
  onBackToGroups: () => void;
  onCreateGroup?: () => void;
  highlightMessageId?: string;
}

const MobileChatView = ({
  selectedGroup,
  messages,
  user,
  sendMessage,
  canCreateGroup,
  onBackToGroups,
  onCreateGroup,
  highlightMessageId
}: MobileChatViewProps) => {
  const [newMessage, setNewMessage] = useState('');
  const [jumpToMessageId, setJumpToMessageId] = useState<string | undefined>(highlightMessageId);

  const [replyingToMessageId, setReplyingToMessageId] = React.useState<string | null>(null);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    await sendMessage(newMessage, replyingToMessageId || undefined);
    setNewMessage('');
    setReplyingToMessageId(null);
  };

  const handleJumpToMention = (messageId: string) => {
    console.log('🎯 Mobile: Jumping to mention:', messageId);
    setJumpToMessageId(messageId);
    // Reset after a short delay to allow re-triggering the same message
    setTimeout(() => setJumpToMessageId(undefined), 1000);
  };

  // Handle case where selectedGroup is not available
  if (!selectedGroup) {
    return (
      <div className="h-screen flex flex-col bg-black text-white overflow-hidden">
        {/* Fixed Chat Header */}
        <div className="fixed top-16 left-0 right-0 z-30 bg-gray-900 border-b border-gray-800 h-12">
          <div className="flex items-center px-3 py-2 gap-3 h-full">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackToGroups}
              className="text-white hover:bg-gray-800 p-2 h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-base font-semibold text-white">
              Chat
            </h1>
          </div>
        </div>
        
        {/* Content Area */}
        <div className="flex-1 flex items-center justify-center pt-12">
          <div className="text-center">
            <p className="text-gray-300 mb-4">No chat group selected</p>
            <Button onClick={onBackToGroups} className="bg-sports-green hover:bg-sports-green/90 text-black">
              Back to Groups
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user is the creator of the group
  const isGroupCreator = selectedGroup.created_by === user?.id;
  const chatType: 'open' | 'read_only' = 'open';
  const canSendMessage = isGroupCreator || chatType === 'open';

  return (
    <div className="h-screen bg-black text-white overflow-hidden">
      {/* FIXED CHAT HEADER - Always visible at top */}
      <div className="fixed top-16 left-0 right-0 z-30 bg-gray-900 border-b border-gray-800 h-12">
        <div className="flex items-center px-3 py-2 gap-2 h-full">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBackToGroups}
            className="text-white hover:bg-gray-800 p-2 h-8 w-8 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          
          <h1 className="text-base font-semibold text-white flex-shrink-0">
            Chat
          </h1>
          
          {/* Group Members Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                size="sm" 
                className="bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white h-8 text-xs px-2 flex-shrink min-w-0"
              >
                <Users className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{selectedGroup.chat_name}</span>
                <ChevronDown className="h-3 w-3 ml-1 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              className="w-80 bg-gray-800 border-gray-700 text-white max-h-96 overflow-y-auto z-50"
              align="start"
            >
              <div className="p-0">
                <ChatGroupMembers
                  chatGroupId={selectedGroup.chat_id}
                  canManageMembers={true}
                  currentUserId={user?.id}
                  isCreator={isGroupCreator}
                  chatType={chatType}
                />
              </div>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Mention Navigator */}
          <div className="flex-shrink-0">
            <MentionNavigator
              messages={messages}
              currentUsername={user?.username}
              onJumpToMention={handleJumpToMention}
              isMobile={true}
            />
          </div>
        </div>
      </div>

      {/* MESSAGES AREA - Fixed position with internal scroll ONLY */}
      <div className="fixed top-28 bottom-32 left-0 right-0 bg-gray-900">
        <MessagesList 
          messages={messages}
          selectedGroup={selectedGroup}
          currentUsername={user?.username}
          currentUserId={user?.id}
          chatType={chatType}
          isCreator={isGroupCreator}
          isMobile={true}
          highlightMessageId={jumpToMessageId || highlightMessageId}
          onReplyClick={(messageId) => setReplyingToMessageId(messageId)}
        />
      </div>

      {/* FIXED INPUT AT BOTTOM - Always visible */}
      <div className="fixed bottom-16 left-0 right-0 z-30 bg-gray-800 border-t border-gray-700">
        <ChatInputArea
          canSendMessage={canSendMessage}
          newMessage={newMessage}
          onMessageChange={setNewMessage}
          onSendMessage={handleSendMessage}
          selectedGroup={selectedGroup}
          isMobile={true}
        />
      </div>
    </div>
  );
};

export default MobileChatView;
